import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navbar from '../components/Navbar';
import ProgressBar2 from './ProgressBar2';
import {
    Droplets,
    Wind,
    Wifi,
    Coffee,
    ChevronLeft,
    MapPin,
    Mail,
    Phone,
    FileText,
    Clock,
    Ticket,
    CheckCircle2,
    Leaf
} from 'lucide-react';
import { ShieldCheck, UtensilsCrossed, Bath, Sparkles } from 'lucide-react';

function Booking() {
    const location = useLocation();
    const navigate = useNavigate();

    const [bookingDetails, setBookingDetails] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        country: '',
        zip: '',
        requests: '',
        arrival: 'I do not know',
        coupon: '',
        terms: false
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    if (!location.state || !location.state.room) {
        navigate('/');
        return null;
    }

    const { room, guests } = location.state;

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setBookingDetails(prevState => ({
            ...prevState,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleBooking = (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        if (!bookingDetails.terms) {
            setError('Please accept the terms and conditions');
            setLoading(false);
            return;
        }

        // Save booking details locally and navigate to checkout
        const bookingData = {
            ...bookingDetails,
            roomName: room.name,
            checkIn: room.checkIn,
            checkOut: room.checkOut,
            totalPrice: room.totalPrice,
            guests: guests,
        };

        // Store in localStorage for persistence
        localStorage.setItem('bookingData', JSON.stringify(bookingData));

        // Navigate to checkout with booking details
        setTimeout(() => {
            setLoading(false);
            navigate('/river/checkout', {
                state: {
                    room,
                    guests,
                    bookingDetails
                }
            });
        }, 500);
    };

    // Removed getServiceIcon helper; using standardized Room Includes icons below.

    return (
        <div className="min-h-screen bg-gradient-to-b from-amber-50 via-white to-green-50">
            <Navbar />
            <ProgressBar2 currentStep="booking" />

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
                    {/* Booking Summary - Sticky on Desktop */}
                    <div className="lg:col-span-1">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4 }}
                            className="bg-white rounded-2xl shadow-lg p-6 lg:sticky lg:top-4 border-l-4 border-green-700"
                        >
                            <h3 className="text-xl md:text-2xl font-light text-gray-900 mb-4 flex items-center">
                                <CheckCircle2 className="w-6 h-6 mr-2 text-green-700" />
                                Booking Summary
                            </h3>

                            {/* Room Image */}
                            <img
                                src={room.image}
                                alt={room.name}
                                className="w-full h-48 md:h-56 object-cover rounded-xl mb-4"
                            />

                            {/* Room Details */}
                            <div className="space-y-3">
                                <div>
                                    <h4 className="text-lg md:text-xl font-light text-green-900 mb-2">
                                        {room.name}
                                    </h4>
                                    <span className="inline-block px-3 py-1 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
                                        Booking Confirmation
                                    </span>
                                </div>

                                <div className="border-t border-gray-200 pt-3 space-y-2 text-sm md:text-base">
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Check In:</span>
                                        <span className="font-medium text-gray-900">{new Date(room.checkIn).toLocaleDateString()}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Check Out:</span>
                                        <span className="font-medium text-gray-900">{new Date(room.checkOut).toLocaleDateString()}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Nights:</span>
                                        <span className="font-medium text-gray-900">{room.nights}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Guests:</span>
                                        <span className="font-medium text-gray-900">{guests} Adult{guests > 1 ? 's' : ''}</span>
                                    </div>
                                    <div className="flex justify-between pt-2 border-t">
                                        <span className="text-gray-600">Price/Night:</span>
                                        <span className="font-medium text-gray-900">€{room.price}</span>
                                    </div>
                                    <div className="flex justify-between font-bold text-lg bg-gradient-to-r from-amber-50 to-green-50 rounded-lg p-3 -mx-3 px-3">
                                        <span className="text-green-900">Total Price:</span>
                                        <span className="text-green-700">€{room.totalPrice.toFixed(2)}</span>
                                    </div>
                                    <p className="text-xs text-amber-600 pt-2 italic">
                                        ℹ️ 18% VAT already applied
                                    </p>
                                </div>

                                {/* Standardized Room Includes (match Booking.jsx) */}
                                <div className="border-t border-gray-200 pt-3">
                                    <p className="text-sm font-medium text-gray-700 mb-3 flex items-center">
                                        <Leaf className="w-4 h-4 mr-2 text-green-700" />
                                        Room Includes:
                                    </p>
                                    <div className="grid grid-cols-2 gap-2">
                                        {[
                                            { icon: Wifi, text: 'Free Wifi' },
                                            { icon: Wind, text: 'Air Conditioning' },
                                            { icon: Droplets, text: 'Hot water' },
                                            { icon: Coffee, text: 'Mini fridge' },
                                            { icon: ShieldCheck, text: 'Safety deposit box' },
                                            { icon: UtensilsCrossed, text: 'Electric kettle' },
                                            { icon: Sparkles, text: 'Daily housekeeping' },
                                            { icon: Bath, text: 'Hair dryer' }
                                        ].map((item, index) => (
                                            <div key={index} className="flex items-center gap-2 text-xs md:text-sm text-gray-600 bg-green-50 rounded-lg p-2">
                                                <span className="text-green-600 flex-shrink-0">
                                                    <item.icon className="w-5 h-5" />
                                                </span>
                                                <span>{item.text}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </div>

                    {/* Booking Form */}
                    <div className="lg:col-span-2">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4, delay: 0.1 }}
                            className="bg-white rounded-2xl shadow-lg p-6 md:p-8 border-l-4 border-green-700"
                        >
                            <h2 className="text-2xl md:text-3xl font-light text-gray-900 mb-6">
                                Guest Details
                            </h2>

                            {error && (
                                <motion.div
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 flex items-start gap-3"
                                >
                                    <span className="text-xl">⚠️</span>
                                    <p>{error}</p>
                                </motion.div>
                            )}

                            <form onSubmit={handleBooking} className="space-y-6">
                                {/* Name Section */}
                                <div>
                                    <label className="block text-sm md:text-base font-medium text-gray-900 mb-3">
                                        Full Name
                                    </label>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        <input
                                            type="text"
                                            name="firstName"
                                            value={bookingDetails.firstName}
                                            onChange={handleChange}
                                            placeholder="First Name"
                                            required
                                            className="w-full px-4 py-2 md:py-3 border border-gray-300 rounded-lg hover:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm md:text-base transition-colors"
                                        />
                                        <input
                                            type="text"
                                            name="lastName"
                                            value={bookingDetails.lastName}
                                            onChange={handleChange}
                                            placeholder="Last Name"
                                            required
                                            className="w-full px-4 py-2 md:py-3 border border-gray-300 rounded-lg hover:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm md:text-base transition-colors"
                                        />
                                    </div>
                                </div>

                                {/* Contact Section */}
                                <div>
                                    <label className="block text-sm md:text-base font-medium text-gray-900 mb-3">
                                        Contact Information
                                    </label>
                                    <div className="space-y-3">
                                        <div className="flex items-center gap-3">
                                            <Mail className="w-5 h-5 text-green-700 flex-shrink-0" />
                                            <input
                                                type="email"
                                                name="email"
                                                value={bookingDetails.email}
                                                onChange={handleChange}
                                                placeholder="Email Address"
                                                required
                                                className="flex-1 px-4 py-2 md:py-3 border border-gray-300 rounded-lg hover:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm md:text-base transition-colors"
                                            />
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <Phone className="w-5 h-5 text-green-700 flex-shrink-0" />
                                            <input
                                                type="tel"
                                                name="phone"
                                                value={bookingDetails.phone}
                                                onChange={handleChange}
                                                placeholder="Phone Number"
                                                required
                                                className="flex-1 px-4 py-2 md:py-3 border border-gray-300 rounded-lg hover:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm md:text-base transition-colors"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Address Section */}
                                <div>
                                    <label className="block text-sm md:text-base font-medium text-gray-900 mb-3">
                                        Address
                                    </label>
                                    <div className="space-y-3">
                                        <div className="flex items-center gap-3">
                                            <MapPin className="w-5 h-5 text-green-700 flex-shrink-0 mt-2" />
                                            <input
                                                type="text"
                                                name="address"
                                                value={bookingDetails.address}
                                                onChange={handleChange}
                                                placeholder="Street Address"
                                                className="flex-1 px-4 py-2 md:py-3 border border-gray-300 rounded-lg hover:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm md:text-base transition-colors"
                                            />
                                        </div>
                                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                            <input
                                                type="text"
                                                name="city"
                                                value={bookingDetails.city}
                                                onChange={handleChange}
                                                placeholder="City"
                                                className="w-full px-4 py-2 md:py-3 border border-gray-300 rounded-lg hover:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm md:text-base transition-colors"
                                            />
                                            <input
                                                type="text"
                                                name="country"
                                                value={bookingDetails.country}
                                                onChange={handleChange}
                                                placeholder="Country"
                                                className="w-full px-4 py-2 md:py-3 border border-gray-300 rounded-lg hover:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm md:text-base transition-colors"
                                            />
                                            <input
                                                type="text"
                                                name="zip"
                                                value={bookingDetails.zip}
                                                onChange={handleChange}
                                                placeholder="ZIP Code"
                                                className="w-full px-4 py-2 md:py-3 border border-gray-300 rounded-lg hover:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm md:text-base transition-colors"
                                            />
                                        </div>
                                    </div>
                                </div>
                                {/* Arrival Time */}
                                <div>
                                    <label className="block text-sm md:text-base font-medium text-gray-900 mb-3">
                                        <div className="flex items-center gap-2">
                                            <Clock className="w-5 h-5 text-green-700" />
                                            Expected Arrival Time
                                        </div>
                                    </label>
                                    <select
                                        name="arrival"
                                        value={bookingDetails.arrival}
                                        onChange={handleChange}
                                        className="w-full px-4 py-2 md:py-3 border border-gray-300 rounded-lg hover:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm md:text-base bg-white transition-colors"
                                    >

                                        <option value="Morning">Morning (6:00 AM - 12:00 PM)</option>
                                        <option value="Afternoon">Afternoon (12:00 PM - 6:00 PM)</option>
                                        <option value="Evening">Evening (6:00 PM - 11:59 PM)</option>
                                    </select>
                                </div>

                                {/* Special Requests */}
                                <div>
                                    <label className="block text-sm md:text-base font-medium text-gray-900 mb-3">
                                        <div className="flex items-center gap-2">
                                            <FileText className="w-5 h-5 text-green-700" />
                                            Special Requests
                                        </div>
                                    </label>
                                    <textarea
                                        name="requests"
                                        value={bookingDetails.requests}
                                        onChange={handleChange}
                                        placeholder="Any special requests or dietary requirements?"
                                        className="w-full px-4 py-3 md:py-4 border border-gray-300 rounded-lg hover:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm md:text-base resize-none h-24 md:h-32 transition-colors"
                                    />
                                </div>

                                {/* Coupon Code */}
                                <div>
                                    <label className="block text-sm md:text-base font-medium text-gray-900 mb-3">
                                        <div className="flex items-center gap-2">
                                            <Ticket className="w-5 h-5 text-green-700" />
                                            Coupon Code (Optional)
                                        </div>
                                    </label>
                                    <input
                                        type="text"
                                        name="coupon"
                                        value={bookingDetails.coupon}
                                        onChange={handleChange}
                                        placeholder="Enter coupon code if you have one"
                                        className="w-full px-4 py-2 md:py-3 border border-gray-300 rounded-lg hover:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm md:text-base transition-colors"
                                    />
                                </div>

                                {/* Terms and Conditions */}
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 md:p-5">
                                    <label className="flex items-start gap-3 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            name="terms"
                                            checked={bookingDetails.terms}
                                            onChange={handleChange}
                                            className="w-5 h-5 mt-1 text-green-600 rounded focus:ring-2 focus:ring-green-500 cursor-pointer"
                                        />
                                        <span className="text-sm md:text-base text-gray-700">
                                            I agree to the{' '}
                                            <a
                                                href="/terms-and-conditions"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-green-700 font-semibold hover:text-green-800 hover:underline transition-colors"
                                            >
                                                Terms and Conditions
                                            </a>
                                            {' '}and the{' '}
                                            <a
                                                href="/privacy-policy"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-green-700 font-semibold hover:text-green-800 hover:underline transition-colors"
                                            >
                                                Privacy Policy
                                            </a>
                                        </span>
                                    </label>
                                </div>

                                {/* Buttons */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-4">
                                    <motion.button
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                        type="button"
                                        onClick={() => navigate(-1)}
                                        className="w-full flex items-center justify-center gap-2 px-4 py-3 md:py-4 bg-gray-300 hover:bg-gray-400 text-gray-800 font-semibold rounded-lg transition-all shadow-md hover:shadow-lg text-sm md:text-base"
                                    >
                                        <ChevronLeft className="w-5 h-5" />
                                        Go Back
                                    </motion.button>
                                    <motion.button
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                        type="submit"
                                        disabled={loading}
                                        className="w-full flex items-center justify-center gap-2 px-4 py-3 md:py-4 bg-green-700 hover:bg-green-800 disabled:bg-green-400 text-white font-semibold rounded-lg transition-all shadow-md hover:shadow-lg text-sm md:text-base"
                                    >
                                        {loading ? (
                                            <>
                                                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                                Processing...
                                            </>
                                        ) : (
                                            <>
                                                <CheckCircle2 className="w-5 h-5" />
                                                Proceed to Checkout
                                            </>
                                        )}
                                    </motion.button>
                                </div>
                            </form>
                        </motion.div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Booking;
